import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  Link,
  SearchInput,
  Space,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  useColorMode,
  useColorStyle,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@tonic-one/react';
import { CloseSIcon } from '@tonic-one/react-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import DelModal from 'src/pages/Report/ActionModal/del';
import { useToggle } from '@tonic-one/react-hooks';
import { GlobalApi } from '@Apis';
import ErrorAlert from '@Components/Common/ErrorAlert';
import { useDisclosure } from '@Components/Common/Tooltip/TextEllipsis/utils';
import CSpinner from '@Components/CSpinner';
import IconButton from '@Components/IconButton';
import config from 'src/config';
import {
  FileType,
  HighProfileUsers,
  HighProfileUsersDto,
} from '@Types/globalSetting';
import SmartTooltip from '../../../Report/SmartTooltip';
import { useOrgStore } from '@Models/useOrgModel';
import { Info } from '@Components/Common/Info';
import common from '@Utils/common';
import { Parser } from '@json2csv/plainjs';
import { ManualAddUserModal } from './ManualAddUserModal';
import AddUserFromOrgModal from './AddUserFromOrgModal';
import AlertModal from '@Components/Common/Modal/AlertModal';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import {
  validateEmail,
  validateHpuName,
  validateIsNotEmptyString,
} from 'src/utils/validate';
import ImportModal from '@Policy/AdvancedThreatProtection/PolicyTemplate/Components/ImportModal';
import { useGlobalSettingStore } from '@Models/useGlobalSettingModel';
import { DetectionThresholdModal } from './DetectionThresholdModal';

const HighProfileUser = () => {
  const [colorMode] = useColorMode();
  const { orgsMap, orgId } = useOrgStore();
  const { widthEditablePermission } = usePermissionStore();
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const [items, setItems] = useState<HighProfileUsers[]>([]);
  const [allItems, setAllItems] = useState<HighProfileUsers[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [delModalTitle, setDelModalTitle] = useState<React.ReactNode>();
  const [isImportModalVisible, setImportModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isAlertModalOpen, toggleAlertModalOpen] = useToggle(false);
  const [delModalContent, setDelModalContent] = useState<
    JSX.Element[] | undefined
  >(undefined);
  const [alertMessage, setAlertMessage] = React.useState<{
    title: string;
    content: JSX.Element;
  }>();
  const [isAddModalOpen, toggleAddModalOpen] = useToggle(false);
  const [delLoading, setDelLoading] = useState(false);
  const [isThresholdModalOpen, setThresholdModalOpen] = useState(false);

  const highProfileUserMaxCount = useMemo(() => {
    return (
      (selectedOrgGlobalSetting.highProfileUserMaxCount as number) ??
      config.ui.globalSettings.limitation.highProfileUserMaxCount
    );
  }, [selectedOrgGlobalSetting]);

  const search = (keyword: string) => {
    if (keyword !== '') {
      const temp = allItems?.filter(
        (item) => item.email.toLowerCase().indexOf(keyword.toLowerCase()) > -1
      );
      setItems(temp);
    } else {
      setItems(allItems);
    }
  };
  const hoverBackgroundColor = (
    {
      dark: 'rgba(255, 255, 255, 0.12)',
      light: 'rgba(0, 0, 0, 0.12)',
    } as any
  )[colorMode];

  async function loadData() {
    try {
      setLoading(true);
      const data: HighProfileUsers[] = (await GlobalApi.getHighProfileUsers())
        .data;
      setItems(data);
      setAllItems(data);
    } catch (error: any) {
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async function doCall() {
      await loadData();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: ColumnDef<HighProfileUsers>[] = useMemo(
    () => [
      {
        id: 'selection',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 48,
      },
      {
        id: 'firstName',
        header: t('globalSetting.highProfileUsers.column.firstName'),
        accessorKey: 'firstName',
        size: 100,
      },
      {
        id: 'middleName',
        header: t('globalSetting.highProfileUsers.column.middleName'),
        accessorKey: 'middleName',
        size: 100,
      },
      {
        id: 'lastName',
        header: t('globalSetting.highProfileUsers.column.lastName'),
        accessorKey: 'lastName',
        size: 150,
      },
      {
        id: 'email',
        header: t('globalSetting.highProfileUsers.column.emailAddress'),
        accessorKey: 'email',
        size: 200,
      },
      {
        id: 'orgId',
        header: t('globalSetting.highProfileUsers.column.addedFrom'),
        cell: ({ row }) => {
          const { orgId } = row.original;
          const orgName = orgsMap.get(orgId);
          return orgName ? orgName : '';
        },
        size: 200,
      },
      {
        id: 'wsTrainingStatus',
        header: () => (
          <Flex alignItems="center" width="100%">
            <Box mr={8} width="calc(100% - 24px)">
              <SmartTooltip>
                {t('globalSetting.highProfileUsers.column.writingStyleStatus')}
              </SmartTooltip>
            </Box>
            <Info
              content={t(
                'globalSetting.highProfileUsers.column.writingStyleStatus.tooltip'
              )}
            />
          </Flex>
        ),
        cell: ({ row }) => {
          const { wsTrainingStatus } = row.original;
          return wsTrainingStatus
            ? t(
                'globalSetting.highProfileUsers.column.writingStyleStatus.status.complete'
              )
            : t(
                'globalSetting.highProfileUsers.column.writingStyleStatus.status.inProcess'
              );
        },
        size: 250,
        meta: {
          needOverflowTooltip: false,
        },
      },
    ],
    []
  );
  const [rowSelection, setRowSelection] = useState({});
  const table = useReactTable({
    data: items,
    columns,
    defaultColumn: {
      minSize: 40,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) =>
      `${row.firstName}|${row.middleName}|${row.lastName}|${row.email}|${row.orgId}`,
    state: {
      rowSelection,
    },
    enableRowSelection: true, // enable row selection for all rows
    // enableRowSelection: row => row.original.detections > 0, // or enable row selection conditionally per row
    onRowSelectionChange: setRowSelection,
  });
  const cellHeight = 36;
  const headerHeight = 36;
  const rowsToDisplay = 10;
  const tableStyleProps = {
    height: headerHeight + rowsToDisplay * cellHeight,
    // Uncomment the following line to specify the width and enable horizontal scrollbar
    // width: 200,
  };
  const selectedRowCount = Object.keys(rowSelection).length;
  const handleDel = () => {
    setDelModalTitle(t('globalSetting.highProfileUsers.deleteModal.title'));
    const selectedRows = getSelectedRows();
    const content = selectedRows.map((row) => (
      <Text
        key={`${row.firstName}|${row.middleName}|${row.lastName}|${row.email}|${row.orgId}`}
        mb={8}
      >
        {`${row.firstName} ${row.middleName} ${row.lastName} ${row.email}`}
      </Text>
    ));
    console.log(content);
    setDelModalContent(content);
    onOpen();
  };
  const reset = () => {
    setRowSelection({});
    search('');
  };

  function getSelectedRows() {
    const selectedRows: HighProfileUsers[] = [];
    for (const key of Object.keys(rowSelection)) {
      const [firstName, middleName, lastName, email, orgId] = key.split('|');
      const item = items.find(
        (item) =>
          item.email === email &&
          item.orgId === orgId &&
          item.firstName === firstName &&
          item.middleName === middleName &&
          item.lastName === lastName
      );
      if (item) {
        selectedRows.push(item);
      }
    }
    return selectedRows;
  }

  const onImport = () => {
    setImportModalVisible(true);
  };

  function parseCsvData() {
    const selectedRows: HighProfileUsers[] = getSelectedRows();
    return selectedRows.map((item) => {
      return {
        [t('globalSetting.highProfileUsers.column.firstName')]: item.firstName,
        [t('globalSetting.highProfileUsers.column.middleName')]:
          item.middleName,
        [t('globalSetting.highProfileUsers.column.lastName')]: item.lastName,
        [t('globalSetting.highProfileUsers.column.emailAddress')]: item.email,
        [t('globalSetting.highProfileUsers.column.orgId')]: item.orgId,
        [t('globalSetting.highProfileUsers.column.addedFrom')]: orgsMap.get(
          item.orgId
        ),
      };
    });
  }

  const onExport = () => {
    const csvData = new Parser().parse(parseCsvData());
    common.exportFile(
      `${t('globalSetting.highProfileUsers.tab.title')}.csv`,
      csvData,
      'text/csv;charset=utf-8;'
    );
    reset();
  };

  const handleImportHighProfileUsers = async (content: HighProfileUsers[]) => {
    console.log(content);
    const allKeys = content.map(
      (user) =>
        `${user.firstName}|${user.middleName}|${user.lastName}|${user.email}|${user.orgId}`
    );
    setImportModalVisible(false);
    await handleAdd(allKeys);
  };

  const handleAdd = async (values: string[]) => {
    if (values.length > 0) {
      const users: HighProfileUsersDto[] = [];
      const duplicateUsers: HighProfileUsersDto[] = [];
      const invalidUsers: HighProfileUsersDto[] = [];
      values.forEach((value) => {
        const compareValue = `${value.toLowerCase()}`;
        const isDuplicate = items.some((item) => {
          const itemValue = `${item.firstName}|${item.middleName}|${item.lastName}|${item.email}|${item.orgId}`;
          console.log(itemValue, compareValue);
          return itemValue.toLowerCase() === compareValue;
        });
        const [firstName, middleName, lastName, email, companyGuid] =
          value.split('|');
        if (
          !validateHpuName(firstName) ||
          !validateHpuName(middleName) ||
          !validateHpuName(lastName) ||
          (validateIsNotEmptyString(email) && !validateEmail(email))
        ) {
          invalidUsers.push({
            firstName,
            middleName,
            lastName,
            email: email || '',
            orgId: companyGuid || orgId,
          });
        } else if (!isDuplicate) {
          users.push({
            firstName,
            middleName,
            lastName,
            email: email || '',
            orgId: companyGuid || orgId,
          });
        } else {
          duplicateUsers.push({
            firstName,
            middleName,
            lastName,
            email: email || '',
            orgId: companyGuid || orgId,
          });
        }
      });
      try {
        if (
          invalidUsers.length !== 0 ||
          items.length + users.length > highProfileUserMaxCount
        ) {
          toggleAlertModalOpen(true);
          setAlertMessage({
            title: t('globalSetting.highProfileUsers.add.inValidUser.title'),
            content: (
              <Box>
                <Text>
                  {t('globalSetting.highProfileUsers.add.inValidUser.content', {
                    count: highProfileUserMaxCount,
                  })}
                </Text>
                <Text mb={8}>
                  {t(
                    'globalSetting.highProfileUsers.add.inValidUser.content.1'
                  )}{' '}
                  {`${invalidUsers.length}`}
                </Text>
                <Text>
                  {t(
                    'globalSetting.highProfileUsers.add.inValidUser.content.2'
                  )}
                </Text>
                {invalidUsers.map((user) => (
                  <Text>{`${user.firstName} ${user.middleName} ${
                    user.lastName
                  } ${user.email} ${orgsMap.get(user.orgId)}`}</Text>
                ))}
              </Box>
            ),
          });
          return;
        }
        setLoading(true);
        if (users.length !== 0) {
          await GlobalApi.addHighProfileUsers(users);
          await loadData();
        }
        if (duplicateUsers.length > 0) {
          toggleAlertModalOpen(true);
          setAlertMessage({
            title: t('globalSetting.highProfileUsers.add.success'),
            content: (
              <Box>
                <Text mb={8}>
                  {t('globalSetting.highProfileUsers.add.partSuccess', {
                    count: users.length,
                  })}
                </Text>
                <Text>{t('globalSetting.highProfileUsers.add.duplicate')}</Text>
                {duplicateUsers.map((user) => (
                  <Text>{`${user.firstName} ${user.middleName} ${
                    user.lastName
                  } ${user.email} ${orgsMap.get(user.orgId)}`}</Text>
                ))}
              </Box>
            ),
          });
        } else {
          window.showNotification({
            titleMsg: t('globalSetting.highProfileUsers.add.success'),
            severity: 'success',
          });
        }
      } catch (e: any) {
        const errorDetails = e?.data?.error?.details?.[0];
        if (
          e?.status === 400 &&
          errorDetails?.message === 'email not exist in any provisioned service'
        ) {
          window.showNotification({
            titleMsg: t(
              'globalSetting.highProfileUsers.add.error.notExistThisCompany'
            ),
            severity: 'error',
          });
          return;
        } else if (
          e?.status === 400 &&
          errorDetails?.code === 'DomainNotInInternalDomains'
        ) {
          const skippedUsers = errorDetails?.skippedUsers || [];
          toggleAlertModalOpen(true);
          setAlertMessage({
            title: t('globalSetting.highProfileUsers.add.unsyncedDomain.title'),
            content: (
              <Box>
                <Text mb={8}>
                  {t(
                    'globalSetting.highProfileUsers.add.unsyncedDomain.content'
                  )}
                </Text>
                {skippedUsers.map((user: string, index: number) => (
                  <Text key={index}>{`${user}`}</Text>
                ))}
                <Text mt={16}>
                  <Trans i18nKey="globalSetting.highProfileUsers.add.unsyncedDomain.guide">
                    <Link
                      href={`${config.ui.prefix}/policy/global-settings/internal-domain-list`}
                      target="_blank"
                    >
                      placeholder
                    </Link>
                    placeholder
                  </Trans>
                </Text>
              </Box>
            ),
          });
          return;
        } else {
          window.showNotification({
            titleMsg: t('common.apiException.internalError'),
            severity: 'error',
          });
        }
      } finally {
        setLoading(false);
      }
    }
    return;
  };

  const confirmDel = async () => {
    setDelLoading(true);
    if (!selectedRowCount) return;
    try {
      const selectedRows = getSelectedRows();
      const res = await GlobalApi.deleteHighProfileUsers(selectedRows);
      if (res.data?.deleteCount) {
        reset();
        window.showNotification({
          titleMsg: t('globalSetting.highProfileUsers.delete.success'),
          severity: 'success',
        });
      }
    } catch (error: any) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      onClose();
      setDelLoading(false);
      await loadData();
    }
  };

  return (
    <Box>
      {loading && <CSpinner />}
      <Box>
        <AlertModal
          visible={isAlertModalOpen}
          onClose={() => {
            toggleAlertModalOpen(false);
          }}
          onConfirm={() => {
            toggleAlertModalOpen(false);
          }}
          confirmButtonName={t('common.button.ok')}
          title={alertMessage?.title}
          content={alertMessage?.content}
        ></AlertModal>
        <DelModal
          visible={isOpen}
          onClose={onClose}
          onConfirm={confirmDel}
          loading={delLoading}
          title={delModalTitle}
          content={delModalContent}
          withEditPermission={widthEditablePermission(
            PermissionNameEnum.administrator
          )}
        ></DelModal>
        <ImportModal
          isVisible={isImportModalVisible}
          onClose={() => {
            setImportModalVisible(false);
          }}
          onImport={handleImportHighProfileUsers}
          acceptFileType={FileType.CSV}
          importFields={[
            'firstName',
            'middleName',
            'lastName',
            'email',
            'orgId',
            'addedFrom',
          ]}
        />
        <ManualAddUserModal
          visible={isAddModalOpen}
          allData={allItems.map(
            (row) =>
              `${row.firstName}|${row.middleName}|${row.lastName}|${row.email}|${row.orgId}`
          )}
          onClose={toggleAddModalOpen}
          onSave={handleAdd}
          title={t('globalSetting.DNSDetectionExceptionList.addModal.title')}
        />
        <AddUserFromOrgModal
          withEditPermission={
            !widthEditablePermission(PermissionNameEnum.administrator)
          }
          visible={addModalVisible}
          onReload={loadData}
          orgId={orgId}
          loading={loading}
          onSave={handleAdd}
          setLoading={setLoading}
          onClose={() => {
            setAddModalVisible(false);
          }}
        />
        <DetectionThresholdModal
          visible={isThresholdModalOpen}
          onClose={() => setThresholdModalOpen(false)}
          disabled={!widthEditablePermission(PermissionNameEnum.administrator)}
        />
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <Flex m="16px 0px">
          <Text
            color={colorStyle.color.secondary}
            lineHeight="lg"
            maxWidth={1000}
          >
            {t('globalSetting.highProfileUsers.description', {
              count: highProfileUserMaxCount,
            })}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text>
            <Space>{t('globalSetting.note.title')}</Space>
            <Space width="1x"></Space>
            <Space color={colorStyle.color.secondary}>
              {t('globalSetting.note.applyToAllOrganizations')}
            </Space>
          </Text>
        </Flex>
        <Flex alignItems="center">
          {selectedRowCount !== 0 ? (
            <Box mr={16}>
              <Stack direction="row">
                <Button
                  variant="secondary"
                  onClick={handleDel}
                  disabled={
                    !widthEditablePermission(PermissionNameEnum.administrator)
                  }
                >
                  {t('reportCommon.buttons.delete')}
                </Button>

                <Space width="12px" />
                <Button variant="secondary" onClick={onExport}>
                  {t('common.action.label.export')}
                </Button>
                <Space width="12px" />
                <IconButton onClick={reset}>
                  <Icon as={CloseSIcon}></Icon>
                </IconButton>
                <Box lineHeight="2rem">
                  <Text color={colorStyle.color.tertiary}>
                    {selectedRowCount}&nbsp;
                    {t('common.label.selected')}
                  </Text>
                </Box>
              </Stack>
            </Box>
          ) : null}
          {selectedRowCount === 0 ? (
            <Stack direction="row">
              <Menu>
                <MenuButton
                  variant="primary"
                  disabled={
                    !widthEditablePermission(
                      PermissionNameEnum.administrator
                    ) || items.length >= highProfileUserMaxCount
                  }
                >
                  <Text>{t('common.action.label.add')}</Text>
                </MenuButton>
                <MenuList width="max-content">
                  <MenuItem
                    onClick={() => {
                      toggleAddModalOpen();
                    }}
                  >
                    {t(
                      'globalSetting.highProfileUsers.addMenuItem.addUserManual'
                    )}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAddModalVisible(true);
                    }}
                  >
                    {t(
                      'globalSetting.highProfileUsers.addMenuItem.addUserFromOrg'
                    )}
                  </MenuItem>
                </MenuList>
              </Menu>
              <Space width="12px" />
              <Button
                variant="secondary"
                onClick={onImport}
                disabled={
                  !widthEditablePermission(PermissionNameEnum.administrator) ||
                  items.length >= highProfileUserMaxCount
                }
              >
                {t('common.action.label.import')}
              </Button>
              <Space width="12px" />
              <SearchInput
                placeholder={t(
                  'globalSetting.DNSDetectionExceptionList.column.emailAddress'
                )}
                onChange={(e: any) => {
                  search(e.target.value);
                }}
                onClearInput={() => {
                  search('');
                }}
                width="240px"
              />
              <Space width="12px" />
              <Button
                variant="secondary"
                onClick={() => setThresholdModalOpen(true)}
              >
                {t('globalSetting.highProfileUsers.detectionThreshold.button')}
              </Button>
            </Stack>
          ) : null}
        </Flex>
        <Table mt={28} layout="flexbox" {...tableStyleProps}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const styleProps = {
                    minWidth: header.column.columnDef.minSize,
                    width: header.getSize(),
                  };
                  const needOverflowTooltip =
                    header.column.columnDef.meta?.needOverflowTooltip ?? true;
                  const content = flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  );
                  return (
                    <TableHeaderCell key={header.id} {...styleProps}>
                      {needOverflowTooltip ? (
                        <SmartTooltip>{content}</SmartTooltip>
                      ) : (
                        content
                      )}
                    </TableHeaderCell>
                  );
                })}
              </TableHeaderRow>
            ))}
          </TableHeader>
          <TableBody overflowY="scroll">
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                _hover={{
                  backgroundColor: hoverBackgroundColor,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const styleProps = {
                    minWidth: cell.column.columnDef.minSize,
                    width: cell.column.getSize(),
                  };
                  return (
                    <TableCell key={cell.id} {...styleProps}>
                      <SmartTooltip>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </SmartTooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};
export default HighProfileUser;
